import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { checkOperation } from "@workspace/operations";

import { eightySixApi } from "../api";
import { useKioskContext } from "../kiosk";
import {
	statusKey,
	type EightySixAction,
	type EightySixStatus,
	type EightySixSubject,
} from "./eighty-six";
import type { EightySixContextValue } from "./eighty-six-context";

/**
 * HA-591 — the connected half of the 86 context: what the server says, and the three writes.
 *
 * Named for the source rather than for the reading, because `useEightySix` is the context hook
 * every tile calls; this one is called once, by whoever owns the provider.
 *
 * <para><b>Gated on the permission keys themselves.</b> `staff-terminal-set-86` and
 * `staff-terminal-override-86` carry `stock:86:set` and `stock:86:override`, the same two the
 * endpoints enforce. Hiding an action a person cannot use is a courtesy and the 403 behind it is
 * the rule; naming the two independently would let them drift, and a button that appears and then
 * fails is worse than one that was never offered.</para>
 *
 * <para><b>Propagation is a ten-second poll, not a push.</b> That is the AC's ceiling and the
 * terminal's existing freshness idiom — `use-epos-display` and `use-kiosk-overview` both refetch on
 * the same interval. The EposHub exists server-side, but the terminal has no hub client at all (the
 * `EposHub.invoke` calls in `epos-context/helpers.ts` are still commented out), so a real push is
 * separate work rather than a line in this ticket.</para>
 */
export function useEightySixSource(
	subjects: EightySixSubject[],
): EightySixContextValue {
	const { tenant, venueId, currentUserOperations } = useKioskContext();
	const queryClient = useQueryClient();

	const { data } = useQuery(
		eightySixApi.getStatusOptions(tenant, venueId, subjects),
	);

	const statuses: Record<string, EightySixStatus> = {};
	for (const status of data?.items ?? []) {
		statuses[statusKey(status.productId, status.variantId)] = status;
	}

	const permissions = {
		canSet: checkOperation({
			operation: "staff-terminal-set-86",
			operations: currentUserOperations,
		}),
		canOverride: checkOperation({
			operation: "staff-terminal-override-86",
			operations: currentUserOperations,
		}),
	};

	const mutation = useMutation({
		mutationFn: ({
			action,
			subject,
			note,
		}: {
			action: EightySixAction;
			subject: EightySixSubject;
			note?: string;
		}) => eightySixApi.write(tenant, venueId, action, { ...subject, note }),
		onSuccess: async () => {
			// The status is DERIVED server-side, so it is refetched rather than patched here: an
			// override changes both what the pill says and what a tap does, and reproducing that
			// precedence on the client would be a second implementation to keep in step.
			await queryClient.invalidateQueries({
				queryKey: [tenant, venueId, "86"],
			});
		},
		onError: (error: Error) => {
			toast.error(error.message || "That could not be applied");
		},
	});

	return {
		statuses,
		permissions,
		apply: async (action, subject, note) => {
			await mutation.mutateAsync({ action, subject, note });
		},
		pending: mutation.isPending,
	};
}
